import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../../Utils/ApiServices";

const BRAND = {
  redDeep: "#7A0E0E",
  red: "#B71C1C",
  orange: "#E64A19",
  amber: "#FFB300",
  amberSoft: "#FFE0A3",
  cream: "#FFF8F0",
  creamDeep: "#FBE9D2",
  charcoal: "#2A1810",
  muted: "#8B6F60",
  gradPrimary:
    "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 45%, #E64A19 100%)",
  gradAmber:
    "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)",
  gradCream: "linear-gradient(180deg, #FFF8F0 0%, #FBE9D2 100%)",
};

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const getAccessToken = () => localStorage.getItem("access_token") || "";

const getCustomerId = () =>
  localStorage.getItem("customer_id") ||
  localStorage.getItem("customerId") ||
  localStorage.getItem("logged_customer_id") ||
  localStorage.getItem("user_id") ||
  "";

const formatDollar = (value) => `$${Number(value || 0).toFixed(2)}`;

const getCartLineInfo = (item) => {
  const isCombo = item.line_type === "combo";
  const isSpecial =
    item.line_type === "special" ||
    item.is_special ||
    item.special_id ||
    item.special_name;

  return {
    isCombo,
    isSpecial,
    title: isCombo
      ? item.combo_name || item.item_name || "Combo"
      : item.item_name || "Menu Item",
    typeLabel: isCombo ? "Combo" : isSpecial ? "Special" : "Regular",
  };
};

const CartLineCard = ({
  item,
  index,
  updateQuantity,
  removeItem,
}) => {
  const { isCombo, isSpecial, title, typeLabel } = getCartLineInfo(item);

  return (
    <MotionCard
      key={item.id}
      layout
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{
        delay: index * 0.04,
        type: "spring",
        stiffness: 260,
        damping: 22,
      }}
      whileHover={{ y: -3 }}
      sx={{
        borderRadius: 4,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(122,14,14,0.08)",
        boxShadow: "0 10px 28px rgba(122,14,14,0.08)",
        overflow: "hidden",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: isCombo
            ? BRAND.gradAmber
            : isSpecial
            ? "linear-gradient(135deg, #2E7D32, #66BB6A)"
            : BRAND.gradPrimary,
        },
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ pr: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: BRAND.charcoal,
                    fontSize: "1.02rem",
                  }}
                >
                  {title}
                </Typography>

                <Chip
                  size="small"
                  label={typeLabel}
                  sx={{
                    bgcolor: isCombo
                      ? BRAND.amberSoft
                      : isSpecial
                      ? "#E8F5E9"
                      : BRAND.creamDeep,
                    color: isSpecial ? "#2E7D32" : BRAND.charcoal,
                    fontWeight: 800,
                    fontSize: 11,
                  }}
                />
              </Stack>

              {!isCombo && item.variant_name && (
                <Chip
                  size="small"
                  label={item.variant_name}
                  sx={{
                    mt: 0.75,
                    bgcolor: BRAND.creamDeep,
                    color: BRAND.charcoal,
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                />
              )}

              <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.75 }}>
                Unit Price: {formatDollar(item.unit_price)}
              </Typography>

              {item.notes && (
                <Typography
                  variant="body2"
                  sx={{
                    color: BRAND.muted,
                    fontStyle: "italic",
                    mt: 0.25,
                  }}
                >
                  “{item.notes}”
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={() => removeItem(item.id)}
              aria-label="Remove item"
              sx={{
                color: BRAND.red,
                bgcolor: "rgba(183,28,28,0.08)",
                "&:hover": {
                  bgcolor: "rgba(183,28,28,0.18)",
                  transform: "rotate(8deg)",
                },
                transition: "all .2s",
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>

          {isCombo && Array.isArray(item.combo_items) && item.combo_items.length > 0 && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 3,
                bgcolor: BRAND.cream,
                border: `1px solid ${BRAND.amberSoft}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 900,
                  color: BRAND.charcoal,
                  mb: 1,
                }}
              >
                Included Items
              </Typography>

              <Stack spacing={0.75}>
                {item.combo_items.map((comboItem, comboIndex) => (
                  <Box key={`${comboItem.item_id}-${comboIndex}`}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 800, color: BRAND.charcoal }}
                        >
                          {comboItem.quantity} × {comboItem.item_name}
                          {comboItem.variant_name
                            ? ` (${comboItem.variant_name})`
                            : ""}
                        </Typography>

                        {comboItem.is_free && (
                          <Chip
                            size="small"
                            label="Free"
                            sx={{
                              mt: 0.25,
                              height: 18,
                              bgcolor: "#E8F5E9",
                              color: "#2E7D32",
                              fontWeight: 800,
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 800, color: BRAND.redDeep }}
                      >
                        {formatDollar(comboItem.line_total)}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                p: 0.5,
                borderRadius: 999,
                bgcolor: BRAND.cream,
                border: `1px solid ${BRAND.amberSoft}`,
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  updateQuantity(item, Math.max(0, Number(item.quantity) - 1))
                }
                aria-label="Decrease quantity"
                sx={{
                  bgcolor: "#fff",
                  color: BRAND.redDeep,
                  border: "1px solid rgba(122,14,14,0.15)",
                  "&:hover": { bgcolor: "#fff", transform: "scale(1.08)" },
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>

              <motion.div
                key={item.quantity}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
              >
                <Box
                  sx={{
                    minWidth: 36,
                    textAlign: "center",
                    fontWeight: 800,
                    color: BRAND.charcoal,
                    px: 1,
                  }}
                >
                  {item.quantity}
                </Box>
              </motion.div>

              <IconButton
                size="small"
                onClick={() => updateQuantity(item, Number(item.quantity || 0) + 1)}
                aria-label="Increase quantity"
                sx={{
                  background: BRAND.gradAmber,
                  color: "#fff",
                  boxShadow: "0 4px 10px rgba(255,143,0,0.4)",
                  "&:hover": { transform: "scale(1.08)" },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "1.1rem",
                background: BRAND.gradPrimary,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {formatDollar(item.line_total)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </MotionCard>
  );
};

const CartDrawer = ({
  open = true,
  onClose,
  locationId,
  serviceType,
  refreshTrigger,
  mode = "drawer",
  onCartCountChange,
}) => {
  const navigate = useNavigate();

  const customerId = useMemo(() => getCustomerId(), []);
  const isLoggedIn = localStorage.getItem("is_logged") === "true";

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const getAuthConfig = () => ({
    Authorization: `Bearer ${getAccessToken()}`,
  });

  const itemCount =
    cart?.items?.reduce((acc, item) => acc + Number(item.quantity || 0), 0) || 0;

  useEffect(() => {
    onCartCountChange?.(itemCount);
    localStorage.setItem("cart_item_count", String(itemCount));
    window.dispatchEvent(
      new CustomEvent("cart-count-updated", { detail: { count: itemCount } })
    );
  }, [itemCount, onCartCountChange]);

  const fetchCart = async () => {
    if (!isLoggedIn || !customerId) {
      setCart(null);
      onCartCountChange?.(0);
      return;
    }

    try {
      setLoading(true);

      const response = await ApiServices.get(
        `/api/v1/customers/${customerId}/cart/`,
        getAuthConfig()
      );

      setCart(response?.data || null);
    } catch (error) {
      if (error?.response?.status === 404) {
        setCart(null);
        onCartCountChange?.(0);
      } else {
        showMessage(
          error?.response?.data?.detail || "Failed to load cart.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const syncCartSettings = async () => {
    if (!isLoggedIn || !customerId) return;

    try {
      await ApiServices.patch(
        `/api/v1/customers/${customerId}/cart/`,
        {
          service_type: serviceType,
          location_id: locationId || null,
        },
        getAuthConfig()
      );

      fetchCart();
    } catch (error) {
      if (error?.response?.status !== 404) {
        showMessage(
          error?.response?.data?.detail || "Failed to update cart settings.",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    if (mode === "page" || open) {
      fetchCart();
    }
  }, [open, refreshTrigger, mode]);

  useEffect(() => {
    if ((mode === "page" || open) && cart && serviceType) {
      syncCartSettings();
    }
  }, [serviceType, locationId]);

  const updateQuantity = async (cartItem, nextQty) => {
    if (!cartItem?.id) {
      showMessage("Cart item id not found.", "error");
      return;
    }

    if (nextQty <= 0) {
      removeItem(cartItem.id);
      return;
    }

    try {
      const response = await ApiServices.patch(
        `/api/v1/customers/${customerId}/cart/items/${cartItem.id}/`,
        {
          quantity: nextQty,
          notes: cartItem.notes || "",
        },
        getAuthConfig()
      );

      setCart(response?.data || null);
    } catch (error) {
      showMessage(
        error?.response?.data?.detail || "Failed to update cart item.",
        "error"
      );
    }
  };

  const removeItem = async (cartItemId) => {
    try {
      const response = await ApiServices.delete(
        `/api/v1/customers/${customerId}/cart/items/${cartItemId}/`,
        getAuthConfig()
      );

      setCart(response?.data || null);
      showMessage("Item removed from cart.", "success");
    } catch (error) {
      showMessage(
        error?.response?.data?.detail || "Failed to remove cart item.",
        "error"
      );
    }
  };

  const clearCart = async () => {
    try {
      await ApiServices.delete(
        `/api/v1/customers/${customerId}/cart/`,
        getAuthConfig()
      );

      setCart(null);
      showMessage("Cart cleared successfully.", "success");
    } catch (error) {
      showMessage(
        error?.response?.data?.detail || "Failed to clear cart.",
        "error"
      );
    }
  };

  const handleCheckout = () => {
    onClose?.();
    navigate("/checkout");
  };

  const goToMenu = () => {
    onClose?.();
    navigate("/menu");
  };

  const AmbientBg = () => (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -80,
          right: -60,
          width: 260,
          height: 260,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,179,0,0.35) 0%, rgba(255,179,0,0) 70%)",
          animation: "cd-float-1 14s ease-in-out infinite",
          filter: "blur(8px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          left: -60,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(230,74,25,0.25) 0%, rgba(230,74,25,0) 70%)",
          animation: "cd-float-2 18s ease-in-out infinite",
          filter: "blur(10px)",
        }}
      />
    </Box>
  );

  const cartContent = (
    <Box
      sx={{
        position: "relative",
        height: mode === "drawer" ? "100%" : "auto",
        minHeight: mode === "page" ? "100vh" : "100%",
        display: "flex",
        flexDirection: "column",
        background: BRAND.gradCream,
      }}
    >
      <AmbientBg />

      <Box
        sx={{
          position: "relative",
          p: 2.75,
          background: BRAND.gradPrimary,
          backgroundSize: "200% 200%",
          animation: "cd-gradient-shift 12s ease infinite",
          color: "#fff",
          overflow: "hidden",
          boxShadow:
            "0 8px 32px rgba(122,14,14,0.35), inset 0 -1px 0 rgba(255,255,255,0.12)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background:
              "linear-gradient(90deg, transparent, rgba(255,224,163,0.9), transparent)",
            backgroundSize: "200% 100%",
            animation: "cd-shimmer 4s linear infinite",
          }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.75} alignItems="center">
            <MotionBox
              initial={{ scale: 0.6, rotate: -15, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0.08))",
                border: "1px solid rgba(255,255,255,0.35)",
                display: "grid",
                placeItems: "center",
                backdropFilter: "blur(8px)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
              }}
            >
              <Badge
                badgeContent={itemCount}
                color="warning"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    fontWeight: 800,
                    bgcolor: BRAND.amber,
                    color: BRAND.charcoal,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  },
                }}
              >
                <ShoppingCartIcon sx={{ color: "#fff" }} />
              </Badge>
            </MotionBox>

            <Box>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 800,
                  fontSize: "1.55rem",
                  lineHeight: 1.1,
                }}
              >
                Your Cart
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                <LocalDiningRoundedIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                <Typography variant="caption" sx={{ opacity: 0.92, fontWeight: 600 }}>
                  {itemCount} {itemCount === 1 ? "item" : "items"} • Fresh & ready
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {mode === "drawer" ? (
            <IconButton
              onClick={onClose}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<RestaurantMenuIcon />}
              onClick={goToMenu}
              sx={{
                borderRadius: 999,
                px: 2.25,
                py: 1,
                bgcolor: "#fff",
                color: BRAND.redDeep,
                fontWeight: 800,
                "&:hover": { bgcolor: BRAND.cream },
              }}
            >
              Menu
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
        {!isLoggedIn ? (
          <Box sx={{ p: 3 }}>
            <MotionCard
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{
                borderRadius: 4,
                background: "linear-gradient(135deg, #FFF1E0, #FFE0A3)",
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <LockOutlinedIcon sx={{ color: BRAND.redDeep, fontSize: 48, mb: 2 }} />
                <Typography fontWeight={900} fontSize="1.4rem">
                  Sign in to start ordering
                </Typography>
                <Typography sx={{ mt: 1, mb: 3, color: BRAND.muted }}>
                  Please login or signup to place an order.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={goToMenu}
                  startIcon={<RestaurantMenuIcon />}
                  sx={{
                    borderRadius: 999,
                    py: 1.3,
                    fontWeight: 800,
                    background: BRAND.gradPrimary,
                  }}
                >
                  Browse Menu
                </Button>
              </CardContent>
            </MotionCard>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 280,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: BRAND.red }} />
            <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
              Loading your cart…
            </Typography>
          </Box>
        ) : !cart?.items?.length ? (
          <Box sx={{ p: 3, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Card sx={{ width: "100%", borderRadius: 5, textAlign: "center" }}>
              <CardContent sx={{ p: 4 }}>
                <ShoppingCartIcon sx={{ fontSize: 64, color: BRAND.amber, mb: 2 }} />
                <Typography fontWeight={900} fontSize="1.5rem">
                  Your cart is empty
                </Typography>
                <Typography sx={{ mt: 1, mb: 3, color: BRAND.muted }}>
                  Add items, combos, or specials from the menu.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<RestaurantMenuIcon />}
                  onClick={goToMenu}
                  sx={{ borderRadius: 999, py: 1.3, fontWeight: 800, background: BRAND.gradPrimary }}
                >
                  Explore Menu
                </Button>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
              <Stack spacing={2}>
                <Card
                  sx={{
                    borderRadius: 4,
                    background: "linear-gradient(135deg, #FFF1E0 0%, #FFE0A3 100%)",
                    border: "1px solid rgba(255,143,0,0.35)",
                  }}
                >
                  <CardContent sx={{ py: 1.75 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <LocalOfferIcon sx={{ color: BRAND.redDeep }} />
                      <Box>
                        <Typography fontWeight={800} sx={{ color: BRAND.charcoal }}>
                          Service: {cart?.service_type || serviceType}
                        </Typography>
                        <Typography variant="body2" sx={{ color: BRAND.muted }}>
                          You can confirm service type on checkout page.
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                <AnimatePresence initial={false}>
                  {cart.items.map((item, index) => (
                    <CartLineCard
                      key={item.id}
                      item={item}
                      index={index}
                      updateQuantity={updateQuantity}
                      removeItem={removeItem}
                    />
                  ))}
                </AnimatePresence>
              </Stack>
            </Box>

            <MotionBox
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              sx={{
                p: 2.25,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, #fff 60%)",
                backdropFilter: "blur(12px)",
                borderTop: "1px solid rgba(122,14,14,0.1)",
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Subtotal
                  </Typography>
                  <Typography fontWeight={700}>{formatDollar(cart?.subtotal)}</Typography>
                </Stack>

                {!!Number(cart?.discount || 0) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Total Discount
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart?.discount)}
                    </Typography>
                  </Stack>
                )}

                {cart?.promotion && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Promotion: {cart.promotion.name}
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.promotion.discount_applied)}
                    </Typography>
                  </Stack>
                )}

                {cart?.coupon && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Coupon: {cart.coupon.code || cart.coupon.name}
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.coupon_discount_amount)}
                    </Typography>
                  </Stack>
                )}

                {!!Number(cart?.promotion_discount_amount || 0) && !cart?.promotion && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Promotion Discount
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.promotion_discount_amount)}
                    </Typography>
                  </Stack>
                )}

                {!!Number(cart?.coupon_discount_amount || 0) && !cart?.coupon && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Coupon Discount
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.coupon_discount_amount)}
                    </Typography>
                  </Stack>
                )}

                {!!Number(cart?.loyalty_discount_amount || 0) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Loyalty Discount
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.loyalty_discount_amount)}
                    </Typography>
                  </Stack>
                )}

                {!!Number(cart?.gift_card_amount || 0) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#2E7D32", fontWeight: 700 }}>
                      Gift Card
                    </Typography>
                    <Typography fontWeight={800} sx={{ color: "#2E7D32" }}>
                      - {formatDollar(cart.gift_card_amount)}
                    </Typography>
                  </Stack>
                )}

                {!!Number(cart?.delivery_fee || 0) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Delivery Fee
                    </Typography>
                    <Typography fontWeight={700}>{formatDollar(cart.delivery_fee)}</Typography>
                  </Stack>
                )}

                <Divider sx={{ borderStyle: "dashed", borderColor: "rgba(122,14,14,0.2)" }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ReceiptLongRoundedIcon sx={{ color: BRAND.redDeep }} />
                    <Typography fontWeight={900} fontSize="1.25rem">
                      Total
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: "1.5rem",
                      background: BRAND.gradPrimary,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {formatDollar(cart?.total)}
                  </Typography>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={clearCart}
                    sx={{
                      borderRadius: 999,
                      py: 1.25,
                      fontWeight: 700,
                      borderColor: "rgba(122,14,14,0.3)",
                      color: BRAND.redDeep,
                    }}
                  >
                    Clear Cart
                  </Button>

                  <Button
                    component={motion.button}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    variant="contained"
                    fullWidth
                    startIcon={<ShoppingCartCheckoutIcon />}
                    onClick={handleCheckout}
                    sx={{
                      borderRadius: 999,
                      py: 1.25,
                      fontWeight: 800,
                      background: BRAND.gradPrimary,
                    }}
                  >
                    Checkout Now
                  </Button>
                </Stack>
              </Stack>
            </MotionBox>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 3, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (mode === "page") {
    return (
      <Container maxWidth="md" disableGutters sx={{ py: { xs: 0, md: 3 } }}>
        <Box
          sx={{
            borderRadius: { xs: 0, md: 5 },
            overflow: "hidden",
            boxShadow: { md: "0 30px 80px rgba(122,14,14,0.18)" },
          }}
        >
          {cartContent}
        </Box>
      </Container>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          borderTopLeftRadius: { sm: 28 },
          borderBottomLeftRadius: { sm: 28 },
          overflow: "hidden",
          boxShadow: "-24px 0 60px rgba(122,14,14,0.25)",
          background: "transparent",
        },
      }}
    >
      {cartContent}
    </Drawer>
  );
};

export default CartDrawer;